/**
 * Physical bounds for liquid-equivalent precipitation (mm).
 *
 * CLAUDE.md §4 (unit consistency): the climate pipeline carries precipitation
 * in millimetres throughout. These named constants are the single source of
 * truth for the physically-plausible envelope, shared by the Open-Meteo
 * provider (per-month source guard) and the SED compiler (annual aggregate
 * guard) so the same number is never re-typed as a magic literal in two
 * places.
 *
 * Reference points (annual liquid-equivalent precipitation):
 *  - Typical continental / data-centre candidate sites: ~300–1,500 mm/yr.
 *  - Tropical monsoon maxima:                            ~3,000–5,000 mm/yr.
 *  - Wettest inhabited places on Earth (Mawsynram /
 *    Cherrapunji, India; Lloró, Colombia):              ~11,000–11,900 mm/yr.
 *
 * The bounds below sit just OUTSIDE the wettest real-world records, so any
 * value exceeding them is dimensionally impossible and is the fingerprint of
 * an aggregation/unit bug (e.g. summing a multi-year daily series without
 * dividing by years-of-record, which inflates totals ~10×).
 */

/**
 * Hard upper bound on a physically-possible ANNUAL precipitation total (mm).
 * Above this, the value cannot be a real climate normal — it is rejected
 * (set to NaN) rather than silently rendered. The wettest places on Earth
 * top out at ~11,900 mm/yr; 12,000 mm/yr is the slimmest defensible ceiling.
 */
export const MAX_PHYSICAL_ANNUAL_PRECIP_MM = 12_000;

/**
 * Upper bound on a physically-possible MEAN MONTHLY precipitation total (mm).
 * Cherrapunji's wettest single month on record is ~9,300 mm, but that is an
 * extreme single-month event, not a 10-year mean monthly normal; sustained
 * mean monthly normals stay well under 1,000 mm. 700 mm/month is a tight
 * screen for the ~10× aggregation fingerprint while clearing real monsoon
 * normals (~500–650 mm/month).
 */
export const MAX_PHYSICAL_MONTHLY_PRECIP_MM = 700;

/**
 * Lower bound for a *plausible non-zero* annual precipitation total (mm).
 * Hyper-arid sites (Atacama, parts of the Sahara) can legitimately report
 * a handful of mm/yr, so this is used only for range-assertion in tests and
 * narrative band selection — never to reject data.
 */
export const MIN_PLAUSIBLE_ANNUAL_PRECIP_MM = 1;

/**
 * True when `mm` is a finite, physically-plausible annual precipitation total.
 * `0` is allowed (legitimate for hyper-arid sites); negative or NaN is not.
 */
export function isPhysicalAnnualPrecip(mm: number): boolean {
  return Number.isFinite(mm) && mm >= 0 && mm <= MAX_PHYSICAL_ANNUAL_PRECIP_MM;
}

/**
 * True when `mm` is a finite, physically-plausible mean monthly precipitation
 * total. `0` is allowed (dry-season months); negative or NaN is not.
 */
export function isPhysicalMonthlyPrecip(mm: number): boolean {
  return Number.isFinite(mm) && mm >= 0 && mm <= MAX_PHYSICAL_MONTHLY_PRECIP_MM;
}
